import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';

const TEMPLATES = [
  {
    label: 'Nearby restaurants (15 min walk)',
    text: 'What are the best Italian restaurants within a 15-minute walk from here? Return top 10 with name, address, why it’s good.',
  },
  {
    label: 'Competitor stores around a point',
    text: 'List 10 competitor stores near this location for a mid-range fashion brand. Return name, category, and a short positioning note.',
  },
  {
    label: 'Local marketing opportunities',
    text: 'Suggest local marketing opportunities within 10km: events, high-traffic places, and partnership ideas. Provide a bullet list.',
  },
];

export default function MapsGroundingPlayground() {
  const [query, setQuery] = useState(TEMPLATES[0].text);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { text, sources, googleMapsWidgetContextToken }
  const [copied, setCopied] = useState(false);
  const [placeDetailsById, setPlaceDetailsById] = useState({}); // placeId -> { loading, error, place }

  const brandId = useMemo(() => {
    try {
      return (
        localStorage.getItem('currentBrandId') ||
        localStorage.getItem('selectedBrandId') ||
        sessionStorage.getItem('currentBrandId') ||
        sessionStorage.getItem('selectedBrandId') ||
        ''
      );
    } catch (_) {
      return '';
    }
  }, []);

  const run = async () => {
    const q = String(query || '').trim();
    if (!q || loading) return;
    setLoading(true);
    setError('');
    setResult(null);
    setPlaceDetailsById({});
    try {
      const body = { query: q, model: 'gemini-2.5-flash' };
      const latNum = Number(lat);
      const lngNum = Number(lng);
      if (Number.isFinite(latNum) && Number.isFinite(lngNum) && String(lat).trim() && String(lng).trim()) {
        body.latitude = latNum;
        body.longitude = lngNum;
      }

      const resp = await fetch('https://api.glowytics.com/api/ai/gemini/maps-grounding', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const txt = await resp.text();
      let json;
      try { json = JSON.parse(txt); } catch { json = { text: txt }; }
      if (!resp.ok) throw new Error(json?.error || json?.details?.error || json?.details?.message || resp.statusText);

      setResult({
        text: json?.text || '',
        sources: Array.isArray(json?.sources) ? json.sources : [],
        googleMapsWidgetContextToken: json?.googleMapsWidgetContextToken || ''
      });
    } catch (e) {
      setError(e?.message || 'Failed to run maps grounding');
    } finally {
      setLoading(false);
    }
  };

  const loadPlaceDetails = async (placeId) => {
    const pid = String(placeId || '').trim();
    if (!pid) return;
    if (placeDetailsById[pid]?.loading) return;
    setPlaceDetailsById((prev) => ({ ...prev, [pid]: { loading: true, error: '', place: null } }));
    try {
      const resp = await fetch(`https://api.glowytics.com/api/ai/gemini/maps/place-details?placeId=${encodeURIComponent(pid)}`, {
        method: 'GET',
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include'
      });
      const txt = await resp.text();
      let json;
      try { json = JSON.parse(txt); } catch { json = {}; }
      if (!resp.ok) throw new Error(json?.error || json?.details?.error || json?.details?.message || resp.statusText);
      setPlaceDetailsById((prev) => ({ ...prev, [pid]: { loading: false, error: '', place: json?.place || null } }));
    } catch (e) {
      setPlaceDetailsById((prev) => ({ ...prev, [pid]: { loading: false, error: e?.message || 'Failed to load place details', place: null } }));
    }
  };

  const copyToken = async () => {
    const token = String(result?.googleMapsWidgetContextToken || '').trim();
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {
      // ignore
    }
  };

  const [exporting, setExporting] = useState(false);

  const fetchPlaceDetailsForExport = async (placeId) => {
    try {
      // Remove "places/" prefix if present (Google Places API expects just the ID)
      const cleanId = String(placeId || '').replace(/^places\//, '');
      if (!cleanId) return null;
      
      const resp = await fetch(`https://api.glowytics.com/api/ai/gemini/maps/place-details?placeId=${encodeURIComponent(cleanId)}`, {
        method: 'GET',
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include'
      });
      
      if (!resp.ok) {
        console.warn(`Failed to fetch place details for ${cleanId}: ${resp.status}`);
        return null;
      }
      
      const json = await resp.json();
      console.log(`Fetched details for ${cleanId}:`, json?.place?.location);
      return json?.place || null;
    } catch (err) {
      console.error(`Error fetching place details for ${placeId}:`, err);
      return null;
    }
  };

  const exportToExcel = async () => {
    if (!result?.sources?.length || exporting) return;
    
    setExporting(true);
    
    try {
      // Fetch all place details sequentially to avoid rate limiting
      const allDetails = [];
      for (const s of result.sources) {
        if (!s.placeId) {
          allDetails.push(null);
          continue;
        }
        // Use cached if available
        if (placeDetailsById[String(s.placeId)]?.place) {
          allDetails.push(placeDetailsById[String(s.placeId)].place);
          continue;
        }
        // Fetch from API with small delay between requests
        const details = await fetchPlaceDetailsForExport(s.placeId);
        allDetails.push(details);
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      // Build export data
      const data = result.sources.map((s, idx) => {
        const details = allDetails[idx];
        // Convert Place ID to Google Maps URL
        const placeId = s.placeId || '';
        const cleanPlaceId = placeId.replace(/^places\//, ''); // Remove "places/" prefix if present
        const placeUrl = cleanPlaceId ? `https://www.google.com/maps/place/?q=place_id:${cleanPlaceId}` : '';
        
        return {
          '#': idx + 1,
          'Name': details?.displayName || s.title || '',
          'Address': details?.formattedAddress || '',
          'Latitude': details?.location?.latitude ?? '',
          'Longitude': details?.location?.longitude ?? '',
          'Rating': details?.rating ?? '',
          'Reviews': details?.userRatingCount ?? '',
          'Type': details?.primaryTypeDisplayName || details?.primaryType || '',
          'Phone': details?.nationalPhoneNumber || '',
          'Website': details?.websiteUri || '',
          'Google Maps': details?.googleMapsUri || s.uri || '',
          'Place Link': placeUrl
        };
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Locations');
      
      // Auto-width columns
      const colWidths = Object.keys(data[0] || {}).map(key => ({
        wch: Math.min(50, Math.max(key.length, ...data.map(row => String(row[key] || '').length)))
      }));
      ws['!cols'] = colWidths;
      
      XLSX.writeFile(wb, `maps-grounding-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const loadAllPlaceDetails = async () => {
    if (!result?.sources?.length) return;
    for (const s of result.sources) {
      if (s.placeId && !placeDetailsById[String(s.placeId)]?.place) {
        await loadPlaceDetails(s.placeId);
      }
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-5">
          <div className="text-xl font-semibold text-gray-900">Google Maps Grounding (Gemini)</div>
          <div className="text-sm text-gray-500">
            Location-aware answers grounded in Google Maps data. Docs:{' '}
            <a className="underline" href="https://ai.google.dev/gemini-api/docs/maps-grounding" target="_blank" rel="noreferrer">
              maps-grounding
            </a>
          </div>
        </div>

        {!!error && (
          <div className="mb-4 text-xs px-3 py-2 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex flex-wrap items-center gap-2">
            <div className="text-xs text-gray-600">Templates</div>
            {TEMPLATES.map((t) => (
              <button
                key={t.label}
                className="px-3 py-2 text-xs rounded-lg border bg-white hover:bg-gray-50"
                onClick={() => setQuery(t.text)}
                disabled={loading}
              >
                {t.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <button
                className="px-3 py-2 text-xs rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                onClick={run}
                disabled={loading || !String(query || '').trim()}
              >
                {loading ? 'Running…' : 'Run'}
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-600 mb-1">Latitude (optional)</div>
                <input
                  className="w-full px-3 py-2 text-sm border rounded-xl"
                  placeholder="34.050481"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Longitude (optional)</div>
                <input
                  className="w-full px-3 py-2 text-sm border rounded-xl"
                  placeholder="-118.248526"
                  value={lng}
                  onChange={(e) => setLng(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-600 mb-1">Prompt</div>
              <textarea
                className="w-full px-3 py-2 text-sm border rounded-xl min-h-[140px]"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={loading}
              />
              <div className="mt-2 text-[11px] text-gray-500">
                Tip: “within X minutes walk”, “near me”, “within 10km”, “in Kadıköy İstanbul” gibi coğrafi niyet içeren prompt’larda en iyi sonuç verir.
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-900 mb-2">Result</div>
              <div className="whitespace-pre-wrap text-sm rounded-xl border bg-gray-50 p-3 text-gray-900 min-h-[180px]">
                {result?.text || (loading ? 'Working…' : 'Run a query to see grounded results.')}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-900 mb-2">
                Embedded view <span className="text-xs font-normal text-gray-500">(contextual widget token)</span>
              </div>
              {String(result?.googleMapsWidgetContextToken || '').trim() ? (
                <div className="rounded-xl border bg-white p-3">
                  <div className="text-xs text-gray-600 mb-2">
                    Gemini bazen <code>googleMapsWidgetContextToken</code> döndürür. Bu token ile Google’ın “contextual widget”ı render edilebilir
                    (My Business admin paneli değil; public Maps deneyimi).
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="px-3 py-2 text-xs rounded-lg border bg-white hover:bg-gray-50"
                      onClick={copyToken}
                      type="button"
                    >
                      {copied ? 'Copied' : 'Copy token'}
                    </button>
                    <div className="text-[11px] text-gray-500 truncate">
                      {String(result.googleMapsWidgetContextToken).slice(0, 80)}…
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] text-gray-500">
                    Not: Widget’ı UI içinde göstermek için ayrıca “Maps widget renderer” entegrasyonu gerekiyor; istersen bir sonraki adımda ekleyelim.
                    Referans: <a className="underline" href="https://ai.google.dev/gemini-api/docs/maps-grounding" target="_blank" rel="noreferrer">docs</a>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No widget token returned for this query.</div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-gray-900">
                Sources <span className="text-xs font-normal text-gray-500">(Google Maps citations)</span>
                </div>
                {result?.sources?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      className="px-3 py-1.5 text-xs rounded-lg border bg-white hover:bg-gray-50 flex items-center gap-1.5"
                      onClick={loadAllPlaceDetails}
                      type="button"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Load All Details
                    </button>
                    <button
                      className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-wait flex items-center gap-1.5"
                      onClick={exportToExcel}
                      disabled={exporting}
                      type="button"
                    >
                      {exporting ? (
                        <>
                          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading Details...
                        </>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Export Excel
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
              {result?.sources?.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {result.sources.map((s, idx) => (
                    <div key={idx} className="rounded-xl border bg-white p-3">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {s.title || 'Google Maps result'}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 truncate">{s.uri}</div>
                      {!!String(s.placeId || '').trim() && (
                        <div className="mt-1 text-[11px] text-gray-500 truncate">placeId: {s.placeId}</div>
                      )}
                      {!!String(s.placeId || '').trim() && (
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            className="px-2.5 py-1.5 text-xs rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-50"
                            onClick={() => loadPlaceDetails(s.placeId)}
                            disabled={placeDetailsById[String(s.placeId)]?.loading}
                            type="button"
                          >
                            {placeDetailsById[String(s.placeId)]?.loading ? 'Loading…' : 'Show listing'}
                          </button>
                          {!!placeDetailsById[String(s.placeId)]?.error && (
                            <div className="text-xs text-amber-700 truncate">{placeDetailsById[String(s.placeId)]?.error}</div>
                          )}
                        </div>
                      )}

                      {!!String(s.placeId || '').trim() && !!placeDetailsById[String(s.placeId)]?.place && (
                        <div className="mt-3 rounded-xl border bg-gray-50 p-3">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {placeDetailsById[String(s.placeId)]?.place?.displayName || s.title}
                          </div>
                          {placeDetailsById[String(s.placeId)]?.place?.formattedAddress && (
                            <div className="mt-2 p-2 rounded-lg bg-gray-100 border border-gray-200">
                              <div className="text-[11px] font-medium text-gray-500 mb-1">📮 Address</div>
                              <div className="text-xs text-gray-800">
                                {placeDetailsById[String(s.placeId)]?.place?.formattedAddress}
                              </div>
                          </div>
                          )}
                          <div className="mt-2 text-xs text-gray-600">
                            {(placeDetailsById[String(s.placeId)]?.place?.rating != null)
                              ? `⭐ ${placeDetailsById[String(s.placeId)]?.place?.rating} (${placeDetailsById[String(s.placeId)]?.place?.userRatingCount || 0})`
                              : 'Rating: —'}
                          </div>
                          {placeDetailsById[String(s.placeId)]?.place?.location && (
                            <div className="mt-2 p-2 rounded-lg bg-blue-50 border border-blue-100">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[11px] font-medium text-blue-700">📍 Coordinates</span>
                                <button
                                  className="text-blue-500 hover:text-blue-700 text-[10px] flex items-center gap-1"
                                  onClick={() => {
                                    const loc = placeDetailsById[String(s.placeId)]?.place?.location;
                                    if (loc) navigator.clipboard.writeText(`${loc.latitude}, ${loc.longitude}`);
                                  }}
                                  title="Copy coordinates"
                                  type="button"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  Copy
                                </button>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-[11px]">
                                <div>
                                  <span className="text-gray-500">Latitude:</span>
                                  <span className="ml-1 font-mono text-gray-800">{placeDetailsById[String(s.placeId)]?.place?.location?.latitude?.toFixed(6)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Longitude:</span>
                                  <span className="ml-1 font-mono text-gray-800">{placeDetailsById[String(s.placeId)]?.place?.location?.longitude?.toFixed(6)}</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {Array.isArray(placeDetailsById[String(s.placeId)]?.place?.photos) &&
                            placeDetailsById[String(s.placeId)]?.place?.photos?.length ? (
                              <div className="mt-3 grid grid-cols-3 gap-2">
                                {placeDetailsById[String(s.placeId)]?.place?.photos?.slice(0, 3).map((p) => (
                                  <a
                                    key={p.name}
                                    href={`https://api.glowytics.com${p.url}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block"
                                    title="Open photo"
                                  >
                                    <img
                                      src={`https://api.glowytics.com${p.url}`}
                                      alt="Place"
                                      className="w-full h-20 object-cover rounded-lg border"
                                      loading="lazy"
                                    />
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <div className="mt-2 text-xs text-gray-500">
                                Photos: —{' '}
                                <span className="text-[11px]">
                                  (Not: fotoğraflar için server’da <code>GOOGLE_MAPS_API_KEY</code> gerekir.)
                                </span>
                              </div>
                            )}

                          <div className="mt-3 flex flex-wrap gap-2">
                            {!!placeDetailsById[String(s.placeId)]?.place?.googleMapsUri && (
                              <a className="underline text-sm" href={placeDetailsById[String(s.placeId)]?.place?.googleMapsUri} target="_blank" rel="noreferrer">
                                Open listing
                              </a>
                            )}
                            {!!placeDetailsById[String(s.placeId)]?.place?.websiteUri && (
                              <a className="underline text-sm" href={placeDetailsById[String(s.placeId)]?.place?.websiteUri} target="_blank" rel="noreferrer">
                                Website
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="mt-2">
                        <a className="underline text-sm" href={s.uri} target="_blank" rel="noreferrer">
                          Open in Google Maps
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500">No sources yet.</div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 text-[11px] text-gray-500">
          This uses the Gemini “tools” mechanism with <code>googleMaps</code>. See:{' '}
          <a className="underline" href="https://ai.google.dev/gemini-api/docs/maps-grounding" target="_blank" rel="noreferrer">
            Grounding with Google Maps
          </a>
          .
        </div>
      </div>
    </div>
  );
}


